import { useMemo, useState } from 'react'
import { ClientSideTable } from 'Presentation/Components/Table'
import { KTSVG } from '_metronic/helpers'
import clsx from 'clsx'
import moment from 'moment'
import { toast } from 'react-toastify'
import { useProjectStore } from 'Store/Project'
import { shallow } from 'zustand/shallow'
import { useParams } from 'react-router-dom'
import { useJobClosingContext } from './Context'
import { JobClosingDocument, JobClosingStatus } from './types'
import JobClosingDocumentPreview from './components/JobClosingDocumentPreview'
import JobClosingForm from './JobClosingForm'
import UploadSignedModal from './components/UploadSignedModal'
import SignedFilePreviewModal from './components/SignedFilePreviewModal'

const STATUS_LABELS: Record<JobClosingStatus, string> = {
  draft: 'ร่าง',
  issued: 'ออกแล้ว',
  signed: 'เซ็นแล้ว',
}

const STATUS_BADGE: Record<JobClosingStatus, string> = {
  draft: 'badge-light-secondary',
  issued: 'badge-light-primary',
  signed: 'badge-light-success',
}

const JobClosingList: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>()
  const {
    jobClosingDocuments,
    isLoading,
    isSubmitting,
    getDocument,
    uploadSignedDocument,
  } = useJobClosingContext()
  const projectData = useProjectStore(state => state.selected, shallow)
  const getJobClosingSignedFile = useProjectStore(
    state => state.getJobClosingSignedFile,
    shallow
  )

  const defaultCompanyName =
    projectData?.customerTitle ?? projectData?.saleOrder?.customerTitle ?? ''

  const [previewDoc, setPreviewDoc] = useState<JobClosingDocument | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editingDoc, setEditingDoc] = useState<JobClosingDocument | null>(null)
  const [uploadDoc, setUploadDoc] = useState<JobClosingDocument | null>(null)
  const [signedPreviewDoc, setSignedPreviewDoc] = useState<JobClosingDocument | null>(null)
  const [signedFileUrl, setSignedFileUrl] = useState<string | null>(null)
  const [previewSignedFileUrl, setPreviewSignedFileUrl] = useState<string | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const openCreateForm = () => {
    setEditingDoc(null)
    setFormOpen(true)
  }

  const openEditForm = async (doc: JobClosingDocument) => {
    setDetailLoading(true)
    try {
      const detail = await getDocument(doc.id)
      setEditingDoc(detail)
      setFormOpen(true)
    } catch (err: any) {
      toast.error(err?.message ?? 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setDetailLoading(false)
    }
  }

  const openPreview = async (doc: JobClosingDocument) => {
    if (!projectId) return
    setDetailLoading(true)
    try {
      const detail = await getDocument(doc.id)
      setPreviewDoc(detail)
      if (detail.hasSignedFile) {
        const { success, data } = await getJobClosingSignedFile(parseInt(projectId, 10), doc.id)
        if (success && typeof data === 'string') {
          setPreviewSignedFileUrl(data)
        } else {
          setPreviewSignedFileUrl(null)
        }
      } else {
        setPreviewSignedFileUrl(null)
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setDetailLoading(false)
    }
  }

  const closePreview = () => {
    if (previewSignedFileUrl) {
      URL.revokeObjectURL(previewSignedFileUrl)
    }
    setPreviewSignedFileUrl(null)
    setPreviewDoc(null)
  }

  const handleUploadSigned = async (file: File, signedByName: string) => {
    if (!uploadDoc) return
    try {
      await uploadSignedDocument(uploadDoc.id, file, signedByName)
      toast.success('อัปโหลดเอกสารเซ็นแล้วสำเร็จ')
      setUploadDoc(null)
    } catch (err: any) {
      toast.error(err?.message ?? 'อัปโหลดไม่สำเร็จ')
    }
  }

  const openSignedPreview = async (doc: JobClosingDocument) => {
    if (!projectId) return
    setDetailLoading(true)
    try {
      const detail = await getDocument(doc.id)
      setSignedPreviewDoc(detail)
      const { success, data } = await getJobClosingSignedFile(parseInt(projectId, 10), doc.id)
      if (success && typeof data === 'string') {
        setSignedFileUrl(data)
      } else {
        setSignedFileUrl(null)
        toast.error(typeof data === 'string' ? data : 'โหลดไฟล์ลายเซ็นไม่สำเร็จ')
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setDetailLoading(false)
    }
  }

  const closeSignedPreview = () => {
    if (signedFileUrl) {
      URL.revokeObjectURL(signedFileUrl)
    }
    setSignedFileUrl(null)
    setSignedPreviewDoc(null)
  }

  const COLUMN_CONFIGS = useMemo(
    () => [
      {
        Header: 'เลขที่เอกสาร',
        accessor: 'documentNo',
        width: 150,
        minWidth: 150,
        Cell: ({ value }: any) => <span className='text-gray-800 fw-bold'>{value}</span>,
      },
      {
        Header: 'วันที่สร้าง',
        accessor: 'createdAt',
        width: 120,
        minWidth: 120,
        Cell: ({ value }: any) => (
          <span className='text-gray-800'>
            {moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-'}
          </span>
        ),
      },
      {
        Header: 'จำนวน Line',
        accessor: 'lineCount',
        width: 100,
        minWidth: 100,
        Cell: ({ value }: any) => <span className='text-gray-800'>{value}</span>,
      },
      {
        Header: 'สถานะ',
        accessor: 'status',
        width: 100,
        minWidth: 100,
        Cell: ({ value }: { value: JobClosingStatus }) => (
          <span className={clsx('badge', STATUS_BADGE[value])}>{STATUS_LABELS[value]}</span>
        ),
      },
      {
        Header: '',
        accessor: 'action',
        width: 280,
        minWidth: 280,
        Cell: ({ row }: any) => {
          const doc = row.original as JobClosingDocument
          return (
            <div className='d-flex flex-wrap gap-1'>
              {(doc.status === 'issued' || doc.status === 'signed') && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  disabled={detailLoading}
                  onClick={() => openPreview(doc)}>
                  <KTSVG
                    path='/media/icons/duotune/general/gen004.svg'
                    className='svg-icon-3 me-1'
                  />
                  ดูเอกสาร
                </button>
              )}
              {doc.status === 'draft' && (
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  disabled={detailLoading}
                  onClick={() => openEditForm(doc)}>
                  แก้ไข
                </button>
              )}
              {doc.status === 'issued' && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-warning'
                  onClick={() => setUploadDoc(doc)}>
                  อัปโหลดลายเซ็น
                </button>
              )}
              {doc.status === 'signed' && doc.hasSignedFile && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-info'
                  disabled={detailLoading}
                  onClick={() => openSignedPreview(doc)}>
                  แสดงลายเซ็น
                </button>
              )}
            </div>
          )
        },
        disableSortBy: true,
      },
    ],
    [detailLoading, openEditForm, openPreview, openSignedPreview]
  )

  return (
    <>
      <div className='card'>
        <div className='card-header'>
          <div className='card-title'>
            <span className='text-gray-800 fw-bold'>ใบปิดงาน</span>
          </div>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-primary' onClick={openCreateForm}>
              <KTSVG path='/media/icons/duotune/general/gen041.svg' className='svg-icon-2 me-1' />
              สร้างใบปิดงาน
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className='card-body py-15 text-center'>
            <span className='spinner-border text-primary' />
          </div>
        ) : jobClosingDocuments.length === 0 ? (
          <div className='card-body py-15'>
            <div className='d-flex flex-column align-items-center text-center'>
              <KTSVG
                path='/media/icons/duotune/files/fil003.svg'
                className='svg-icon-5x svg-icon-muted mb-6'
              />
              <h3 className='text-gray-800 fw-bold mb-3'>ยังไม่มีใบปิดงาน</h3>
              <p className='text-gray-600 mb-6 max-w-400px'>
                สร้างใบปิดงานใหม่โดยเพิ่มรายการเสาและค่าใช้จ่ายจาก tab นี้
              </p>
              <button type='button' className='btn btn-primary' onClick={openCreateForm}>
                <KTSVG path='/media/icons/duotune/general/gen041.svg' className='svg-icon-2 me-1' />
                สร้างใบปิดงาน
              </button>
            </div>
          </div>
        ) : (
          <div className='card-body px-0'>
            <ClientSideTable
              isGrey
              pagination
              columns={COLUMN_CONFIGS}
              data={jobClosingDocuments}
              items_per_page={10}
            />
          </div>
        )}
      </div>

      <JobClosingDocumentPreview
        isOpen={!!previewDoc}
        projectId={projectId ?? ''}
        division={projectData?.saleOrder?.division ?? projectData?.division ?? ''}
        jobClosingDocument={previewDoc}
        signedFileUrl={previewSignedFileUrl}
        onClose={closePreview}
      />

      <JobClosingForm
        isOpen={formOpen}
        editingDocument={editingDoc}
        defaultCompanyName={defaultCompanyName}
        onClose={() => setFormOpen(false)}
        onSuccess={() => setFormOpen(false)}
      />

      <UploadSignedModal
        isOpen={!!uploadDoc}
        documentNo={uploadDoc?.documentNo}
        isSubmitting={isSubmitting}
        onClose={() => setUploadDoc(null)}
        onSubmit={handleUploadSigned}
      />

      <SignedFilePreviewModal
        isOpen={!!signedPreviewDoc}
        documentNo={signedPreviewDoc?.documentNo}
        fileUrl={signedFileUrl}
        mimeType={signedPreviewDoc?.signedMimeType}
        onClose={closeSignedPreview}
      />
    </>
  )
}

export default JobClosingList
